/**
 * CapsuleThemePlayer — theme-aware capsule player.
 *
 * Single source of truth for "which component plays a capsule."
 * Used by:
 *   • artifacts/mobile/app/capsule/[id].tsx  (full-screen route)
 *   • artifacts/mobile/components/VideoFeedPlayer.tsx  (in-feed overlay)
 *
 * Routing:
 *   html-bridge                        → BridgeThemePlayScreen (iFrame HTML theme)
 *   liquid-glass | summer | pixel-art  → NativeThemePlayScreen (RN theme with own PlayerComponent)
 *   classic-quiz (default)             → SlidePlayer
 */
import {type ReactNode, useCallback, useEffect, useRef, useState} from 'react'
import {StyleSheet, Text, TouchableOpacity, View} from 'react-native'
import {Feather} from '@expo/vector-icons'
import {Trans} from '@lingui/react/macro'

import {getCapsuleMaxPoints, getSlideMaxPoints} from '#/lib/scoring'
import {useCreatePlayMutation} from '#/state/queries/create-play'
import {useSession} from '#/state/session'
import {CompletionScreen} from '#/components/capsules/CompletionScreen'
import {SlidePlayer} from '#/components/capsules/SlidePlayer'
import {ThemeRenderer} from '#/components/capsules/ThemeRenderer'
import {getTheme} from '#/themes/registry'
import {
  type CapsuleModel,
  type PageResult,
  type SlideResult,
} from '#/types/capsule'

// ── Theme-set constants (shared with [id].tsx) ────────────────────────────────

/** Themes whose player is an HTML iframe bridge — must go through ThemeRenderer. */
export const BRIDGE_RENDERED_THEMES = new Set(['html-bridge'])

/**
 * Native (React Native) themes that have their own PlayerComponent.
 * They call onCapsuleComplete(results) when done; the host shows CompletionScreen.
 */
export const NATIVE_CUSTOM_THEMES = new Set([
  'liquid-glass',
  'summer',
  'pixel-art',
])

// ── Shared prop type ──────────────────────────────────────────────────────────

export interface CapsuleThemePlayerProps {
  capsule: CapsuleModel
  /** true when called from the teacher's Tester/preview flow */
  isPreview?: boolean
  /** footer rendered on the completion screen in preview mode */
  previewFooter?: ReactNode
  /** safe-area top inset, used to position the preview banner */
  insetsTop?: number
  /** index of the slide to start at (default 0) */
  initialSlideIdx?: number
  /** if true, only play the slide at initialSlideIdx (creator per-slide preview) */
  isSingleSlide?: boolean
  onClose: () => void
}

// ── Main exported component ───────────────────────────────────────────────────

export function CapsuleThemePlayer({
  capsule,
  isPreview = false,
  previewFooter,
  insetsTop = 0,
  initialSlideIdx = 0,
  isSingleSlide = false,
  onClose,
}: CapsuleThemePlayerProps) {
  // ── Bridge-rendered themes (html-bridge) ────────────────────────────────────
  if (BRIDGE_RENDERED_THEMES.has(capsule.themeId)) {
    const startIdx = isSingleSlide ? Math.max(0, initialSlideIdx) : 0
    const capsuleForPlayer = isSingleSlide
      ? {...capsule, slides: capsule.slides.slice(startIdx, startIdx + 1)}
      : capsule
    const bridgeMode: 'play' | 'preview' | 'creator' = isSingleSlide
      ? 'creator'
      : isPreview
        ? 'preview'
        : 'play'

    return (
      <BridgeThemePlayScreen
        capsule={capsuleForPlayer}
        isPreview={isPreview}
        previewFooter={previewFooter}
        insetsTop={insetsTop}
        mode={bridgeMode}
        onClose={onClose}
      />
    )
  }

  // ── Native custom themes (liquid-glass, summer, pixel-art) ──────────────────
  if (NATIVE_CUSTOM_THEMES.has(capsule.themeId)) {
    const startIdx = isSingleSlide ? Math.max(0, initialSlideIdx) : 0
    const capsuleForPlayer = isSingleSlide
      ? {...capsule, slides: capsule.slides.slice(startIdx, startIdx + 1)}
      : capsule
    const nativeMode: 'play' | 'preview' | 'creator' = isSingleSlide
      ? 'creator'
      : isPreview
        ? 'preview'
        : 'play'

    return (
      <NativeThemePlayScreen
        capsule={capsuleForPlayer}
        isPreview={isPreview}
        previewFooter={previewFooter}
        insetsTop={insetsTop}
        mode={nativeMode}
        onClose={onClose}
      />
    )
  }

  // ── classic-quiz / unrecognised → generic SlidePlayer ──────────────────────
  return (
    <View style={s.root}>
      <SlidePlayer
        capsule={capsule}
        isPreview={isPreview}
        initialSlideIdx={initialSlideIdx}
        onClose={onClose}
        footer={previewFooter}
      />
      {/* Preview banner overlay (SlidePlayer doesn't render it itself) */}
      {isPreview && (
        <View
          pointerEvents="box-none"
          style={[StyleSheet.absoluteFill, {paddingTop: insetsTop + 4}]}>
          <View style={s.previewBanner} pointerEvents="none">
            <Feather name="eye" size={12} color="#ffc107" />
            <Text style={s.previewBannerTxt}>
              <Trans>PREVIEW — no data saved</Trans>
            </Text>
          </View>
        </View>
      )}
    </View>
  )
}

// ── Internal sub-screens ──────────────────────────────────────────────────────

interface ThemeScreenProps {
  capsule: CapsuleModel
  isPreview: boolean
  previewFooter: ReactNode
  insetsTop: number
  mode: 'play' | 'preview' | 'creator'
  onClose: () => void
}

/**
 * Bridge-rendered theme player.
 * The HTML theme manages its own result screen (Protocol v3); when done it
 * sends an `exit` event and ThemeRenderer calls onCapsuleComplete. We simply
 * navigate away — no second CompletionScreen.
 */
function BridgeThemePlayScreen({
  capsule,
  isPreview,
  previewFooter: _previewFooter,
  insetsTop,
  mode,
  onClose,
}: ThemeScreenProps) {
  const {mutate: savePlay} = useCreatePlayMutation()
  const {currentAccount} = useSession()
  const isOwnCapsule = currentAccount?.did === capsule.creatorId

  const handleComplete = useCallback(
    (pageResults: PageResult[]) => {
      if (mode === 'play' && !isOwnCapsule) {
        const slideMap = new Map(capsule.slides.map(sl => [sl.id, sl]))
        const scored: SlideResult[] = pageResults.map(pr => {
          const slide = slideMap.get(pr.pageId)
          const maxPts = slide ? getSlideMaxPoints(slide) : 1
          return {
            slideId: pr.pageId,
            type: slide?.type ?? 'content',
            isCorrect: pr.isCorrect,
            timeSpentMs: pr.timeSpentMs,
            userAnswer: pr.userAnswer,
            points: pr.isCorrect ? maxPts : 0,
          }
        })
        const totalPts = scored.reduce((sum, s) => sum + (s.points ?? 0), 0)
        const totalMs = pageResults.reduce((sum, r) => sum + r.timeSpentMs, 0)
        savePlay({
          capsuleUri: capsule.id,
          score: totalPts,
          timeMs: totalMs,
          slideResults: scored,
        })
      }
      onClose()
    },
    [mode, capsule, savePlay, onClose, isOwnCapsule],
  )

  return (
    <View style={s.root}>
      <ThemeRenderer
        capsule={capsule}
        currentPageIndex={0}
        onPageComplete={() => {}}
        onCapsuleComplete={handleComplete}
        mode={mode}
      />

      {isPreview && (
        <View
          pointerEvents="box-none"
          style={[StyleSheet.absoluteFill, {paddingTop: insetsTop + 4}]}>
          <View pointerEvents="box-none" style={s.previewTopRow}>
            <TouchableOpacity
              accessibilityRole="button"
              onPress={onClose}
              style={s.previewCloseBtn}
              hitSlop={{top: 12, bottom: 12, left: 12, right: 12}}>
              <Feather name="arrow-left" size={18} color="#fff" />
              <Text style={s.previewCloseTxt}>
                <Trans>Editor</Trans>
              </Text>
            </TouchableOpacity>
            <View style={s.previewBanner} pointerEvents="none">
              <Feather name="eye" size={12} color="#ffc107" />
              <Text style={s.previewBannerTxt}>
                <Trans>PREVIEW</Trans>
              </Text>
            </View>
            <View style={s.previewCloseSpacer} pointerEvents="none" />
          </View>
        </View>
      )}
    </View>
  )
}

/**
 * Native-theme player.
 * Renders the theme's own PlayerComponent via ThemeRenderer. When
 * onCapsuleComplete fires the host captures results and shows CompletionScreen.
 */
function NativeThemePlayScreen({
  capsule,
  isPreview,
  previewFooter,
  insetsTop,
  mode,
  onClose,
}: ThemeScreenProps) {
  const [results, setResults] = useState<PageResult[] | null>(null)
  const [playKey, setPlayKey] = useState(0)
  const [timeTakenMs, setTimeTakenMs] = useState(0)
  const sessionStartRef = useRef(0)
  const {mutate: savePlay} = useCreatePlayMutation()
  const {currentAccount} = useSession()
  const isOwnCapsule = currentAccount?.did === capsule.creatorId

  useEffect(() => {
    sessionStartRef.current = Date.now()
  }, [playKey])

  let primaryColor = '#14b8a6'
  try {
    primaryColor = getTheme(capsule.themeId).previewColor
  } catch {
    /* default */
  }

  const handleComplete = useCallback(
    (r: PageResult[]) => {
      const elapsed = Date.now() - sessionStartRef.current
      setTimeTakenMs(elapsed)
      setResults(r)
      if (mode === 'play' && !isOwnCapsule) {
        const slideMap = new Map(capsule.slides.map(sl => [sl.id, sl]))
        const scored: SlideResult[] = r.map(pr => {
          const slide = slideMap.get(pr.pageId)
          const maxPts = slide ? getSlideMaxPoints(slide) : 1
          return {
            slideId: pr.pageId,
            type: slide?.type ?? 'content',
            isCorrect: pr.isCorrect,
            timeSpentMs: pr.timeSpentMs,
            userAnswer: pr.userAnswer,
            points: pr.isCorrect ? maxPts : 0,
          }
        })
        const totalPts = scored.reduce((sum, s) => sum + (s.points ?? 0), 0)
        savePlay({
          capsuleUri: capsule.id,
          score: totalPts,
          timeMs: elapsed,
          slideResults: scored,
        })
      }
    },
    [mode, capsule, savePlay, isOwnCapsule],
  )

  if (results) {
    const slideMap = new Map(capsule.slides.map(sl => [sl.id, sl]))
    const scored: SlideResult[] = results.map(r => {
      const slide = slideMap.get(r.pageId)
      const maxPts = slide ? getSlideMaxPoints(slide) : 1
      return {
        slideId: r.pageId,
        type: slide?.type ?? 'content',
        isCorrect: r.isCorrect,
        timeSpentMs: r.timeSpentMs,
        userAnswer: r.userAnswer,
        points: r.isCorrect ? maxPts : 0,
      }
    })
    const exerciseResults = scored.filter(r => r.isCorrect !== undefined)
    const correct = exerciseResults.filter(r => r.isCorrect).length
    const totalPts = scored.reduce((sum, r) => sum + (r.points ?? 0), 0)
    const maxTotalPts = getCapsuleMaxPoints(capsule.slides)

    return (
      <View style={s.root}>
        <CompletionScreen
          score={correct}
          total={Math.max(exerciseResults.length, 1)}
          totalPts={totalPts}
          maxTotalPts={maxTotalPts}
          timeTakenMs={timeTakenMs}
          primaryColor={primaryColor}
          capsuleTitle={capsule.title}
          themeId={capsule.themeId}
          results={scored}
          slides={capsule.slides}
          onReplay={() => {
            setResults(null)
            setTimeTakenMs(0)
            setPlayKey(k => k + 1)
          }}
          onDone={onClose}
          footer={previewFooter}
        />
      </View>
    )
  }

  return (
    <View style={s.root}>
      <ThemeRenderer
        key={playKey}
        capsule={capsule}
        currentPageIndex={0}
        onPageComplete={() => {}}
        onCapsuleComplete={handleComplete}
        mode={mode}
      />

      {isPreview && (
        <View
          pointerEvents="box-none"
          style={[StyleSheet.absoluteFill, {paddingTop: insetsTop + 4}]}>
          <View pointerEvents="box-none" style={s.previewTopRow}>
            <TouchableOpacity
              accessibilityRole="button"
              onPress={onClose}
              style={s.previewCloseBtn}
              hitSlop={{top: 12, bottom: 12, left: 12, right: 12}}>
              <Feather name="arrow-left" size={18} color="#fff" />
              <Text style={s.previewCloseTxt}>
                <Trans>Editor</Trans>
              </Text>
            </TouchableOpacity>
            <View style={s.previewBanner} pointerEvents="none">
              <Feather name="eye" size={12} color="#ffc107" />
              <Text style={s.previewBannerTxt}>
                <Trans>PREVIEW</Trans>
              </Text>
            </View>
            <View style={s.previewCloseSpacer} pointerEvents="none" />
          </View>
        </View>
      )}
    </View>
  )
}

// ── Shared styles ─────────────────────────────────────────────────────────────

const s = StyleSheet.create({
  root: {flex: 1, backgroundColor: '#0a1220'},
  previewTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
  },
  previewCloseBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 20,
    backgroundColor: 'rgba(0,0,0,0.55)',
  },
  previewCloseTxt: {color: '#fff', fontSize: 13, fontWeight: '700'},
  previewCloseSpacer: {width: 80},
  previewBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 20,
    backgroundColor: 'rgba(0,0,0,0.55)',
  },
  previewBannerTxt: {
    color: '#ffc107',
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
})
